// ════════════════════════════════════════════════════════════════════════════
// The `attach-result` authoring grammar — model text in, intent out.
//
//   ``` attach-result
//   title: ...                                  // optional
//   links: [label](url), [label](url)           // one search link, one result
//   ```                                         // link, or many result links
//
// Ruled asymmetry (§7.1, U14):
//
//   PARAMS ARE LENIENT — resolve what parses, drop what doesn't, tolerate
//   syntax noise. A model that indented the block, bulleted the list, wrapped
//   the links over four lines, or invented a `note:` key still gets the attach
//   it asked for.
//
//   LINKS ARE STRICT — but not here. This parser only says which strings were
//   offered as links; whether each one RESOLVES is the write seam's question
//   (U15: valid iff it resolves in the writer's live store), because only the
//   seam holds the store. A parser that also judged validity would answer that
//   question twice, in two places, from two sets of facts.
//
// Pure, capped and drop-counting, following `parse-map-block.ts` — the
// standing fenced-block precedent.
// ════════════════════════════════════════════════════════════════════════════

import { offeredLinkActionAddress } from '../session/orchestration/tool-protocol/follow-up-addresses.js';
import {
  ATTACH_BARE_URL_REGEX,
  ATTACH_BULLET_REGEX,
  ATTACH_FENCE_INFO_STRING,
  ATTACH_MARKDOWN_LINK_REGEX,
  ATTACH_MAX_INPUT_LENGTH,
  ATTACH_MAX_LABEL_LENGTH,
  ATTACH_MAX_LINE_LENGTH,
  ATTACH_MAX_LINKS,
  ATTACH_MAX_TITLE_LENGTH,
  ATTACH_PARAM_REGEX,
  ATTACH_URL_SCHEME_HEAD_REGEX,
} from './attached-result-constants.js';
import { fenceSpansOfKind, type FenceSpan } from './fence-spans.js';

/** The closed param vocabulary. A key outside it is counted and dropped —
 *  never guessed at, and never read as a link. */
export enum AttachBlockParamKey {
  Title = 'title',
  Links = 'links',
}

/** One offered link. `label` is null for a bare URL — the model named the
 *  address without a display name, which is a link with no label, not a
 *  malformed item. */
export interface AttachLinkItem {
  readonly label: string | null;
  readonly url: string;
}

/** One dropped unknown key, and how many `key:` lines carried it. */
export interface AttachDroppedKey {
  readonly key: string;
  readonly count: number;
}

/** What one authored block asked for. */
export interface ParsedAttachBlock {
  readonly title: string | null;
  readonly links: readonly AttachLinkItem[];
  /** Links past `ATTACH_MAX_LINKS`, and repeats of a URL already offered. */
  readonly droppedLinkCount: number;
  /** `key: value` lines whose key is outside `AttachBlockParamKey` — one entry
   *  per distinct key, first-appearance ordered, counting every OCCURRENCE
   *  (eight repeated `Source:` lines are `{key: "source", count: 8}`).
   *  Recognized identically before and after the links region; a line whose
   *  head is itself a URL scheme (`https://…` matches the param shape as key
   *  `https`) is a link line, never an unknown key. */
  readonly droppedKeys: readonly AttachDroppedKey[];
}

/** One located attach block and what it asked for — what the materialiser
 *  splices over. */
export interface LocatedAttachBlock {
  readonly span: FenceSpan;
  readonly parsed: ParsedAttachBlock;
}

/** The parser's working state. Only `parseAttachBlock` mutates it, on its own
 *  local copy; every helper below is a pure function returning a value. */
interface ParseAccumulator {
  title: string | null;
  links: AttachLinkItem[];
  seenUrls: Set<string>;
  droppedLinkCount: number;
  /** Per-key occurrence counts of dropped unknown keys, first-appearance
   *  ordered (a `Map` iterates in insertion order). */
  droppedKeyCounts: Map<string, number>;
  /** True once a `links:` line has opened the region. Bare URLs are read only
   *  inside it: outside, a URL is prose, and prose is not an attach. */
  inLinksRegion: boolean;
}

const PARAM_KEYS: ReadonlySet<string> = new Set(Object.values(AttachBlockParamKey));

/** Strip indentation and one optional markdown bullet — models emit both. */
function normalizeLine(input: { rawLine: string }): string {
  return input.rawLine.trim().replace(ATTACH_BULLET_REGEX, '').trim();
}

/** Every `[label](url)` in `text`, plus the text with those items removed —
 *  so the bare-URL scan that follows cannot re-read a URL it already has. */
function readMarkdownLinks(input: { text: string }): {
  items: AttachLinkItem[];
  remainder: string;
} {
  const items: AttachLinkItem[] = [];
  // `String.replace` and `String.match` both reset a global regex's
  // `lastIndex` before running, so the shared constants are safe to use
  // directly — no per-call clone is needed to keep the scans independent.
  const remainder = input.text.replace(
    ATTACH_MARKDOWN_LINK_REGEX,
    (_match, label: string, url: string) => {
      items.push({ label: readLabel({ rawLabel: label }), url });
      return ' ';
    },
  );
  return { items, remainder };
}

/** Bare `http(s)://…` runs in `text`, plus the text with those runs removed —
 *  so the action-address scan that follows cannot re-read (and count as a
 *  dropped repeat) an address already collected here. Trailing sentence
 *  punctuation is not part of an address — `…/l/x/AbC123, ` offers the link,
 *  not the comma. */
function readBareUrls(input: { text: string }): {
  items: AttachLinkItem[];
  remainder: string;
} {
  const items: AttachLinkItem[] = [];
  const remainder = input.text.replace(ATTACH_BARE_URL_REGEX, (url) => {
    items.push({ label: null, url: trimUrlTail({ url }) });
    return ' ';
  });
  return { items, remainder };
}

const URL_TAIL_PUNCTUATION = /[.,;:!?'"]+$/;

function trimUrlTail(input: { url: string }): string {
  return input.url.replace(URL_TAIL_PUNCTUATION, '');
}

const BARE_WORD_SEPARATOR = /[\s,]+/;

/** Bare ACTION addresses in `text` — the rootless `/r/<token>` path and the
 *  naked grammar token itself (`HOGO-99-1`), the dresses a model produces by
 *  trimming a printed action link down past its host. Read INSIDE the links
 *  value only, exactly like bare URLs, and judged by the ONE shared
 *  link-position reading (`offeredLinkActionAddress`) — the same predicate the
 *  plan seam and the research-address guard consult, so an offered dead token
 *  becomes the guard's ruled refusal instead of surviving as prose (phase-4
 *  validation, required fix 2). A word that reading refuses stays prose. */
function readBareActionAddresses(input: { text: string }): AttachLinkItem[] {
  return input.text
    .split(BARE_WORD_SEPARATOR)
    .map((word) => trimUrlTail({ url: word }))
    .filter((word) => word.length > 0)
    .filter((word) => offeredLinkActionAddress({ url: word }) !== null)
    .map((url) => ({ label: null, url }));
}

/** Append under the ceiling, counting every drop: a repeat of a URL already
 *  offered (one row was named twice) and everything past the cap. */
function collectLink(input: { target: ParseAccumulator; item: AttachLinkItem }): void {
  const { target, item } = input;
  if (item.url.length === 0 || target.seenUrls.has(item.url)) {
    target.droppedLinkCount += 1;
    return;
  }
  if (target.links.length >= ATTACH_MAX_LINKS) {
    target.droppedLinkCount += 1;
    return;
  }
  target.seenUrls.add(item.url);
  target.links.push(item);
}

function collectLinksFrom(input: {
  target: ParseAccumulator;
  text: string;
  allowBareUrls: boolean;
}): void {
  const { items, remainder } = readMarkdownLinks({ text: input.text });
  items.forEach((item) => collectLink({ target: input.target, item }));
  if (!input.allowBareUrls) return;
  const bare = readBareUrls({ text: remainder });
  bare.items.forEach((item) => collectLink({ target: input.target, item }));
  readBareActionAddresses({ text: bare.remainder }).forEach((item) =>
    collectLink({ target: input.target, item }),
  );
}

/** A label is display prose, normalized at THE authoring boundary — every
 *  label enters through here, so the bound holds wherever one later renders
 *  (a card's comment field line, a generic headline, block framing). The
 *  title's posture exactly: truncated, never refused, never silently dropped
 *  (validator Medium 5 — an unbounded label would meet the persisted grammar's
 *  600-char line ceiling and vanish whole). */
function readLabel(input: { rawLabel: string }): string | null {
  const label = input.rawLabel.trim();
  if (label.length === 0) return null;
  return label.length > ATTACH_MAX_LABEL_LENGTH
    ? label.slice(0, ATTACH_MAX_LABEL_LENGTH).trimEnd()
    : label;
}

/** A title is display prose. It is truncated rather than refused: a long
 *  title is a rendering problem, and refusing the write over one would throw
 *  away a correct attach. */
function readTitle(input: { rawValue: string }): string | null {
  const value = input.rawValue.trim();
  if (value.length === 0) return null;
  return value.length > ATTACH_MAX_TITLE_LENGTH
    ? value.slice(0, ATTACH_MAX_TITLE_LENGTH).trimEnd()
    : value;
}

function readParamLine(input: { target: ParseAccumulator; line: string }): boolean {
  const match = input.line.match(ATTACH_PARAM_REGEX);
  if (match === null) return false;
  const key = (match[1] ?? '').toLowerCase();
  const rawValue = match[2] ?? '';
  if (!PARAM_KEYS.has(key)) return false;
  if (key === AttachBlockParamKey.Title) {
    input.target.title = readTitle({ rawValue });
    input.target.inLinksRegion = false;
    return true;
  }
  input.target.inLinksRegion = true;
  collectLinksFrom({ target: input.target, text: rawValue, allowBareUrls: true });
  return true;
}

/** Unknown-key accounting, position-independent and run AFTER the line's link
 *  reading, so counting a key never changes which links are collected. A line
 *  whose head is itself a URL scheme is exempt: `https://…` matches the param
 *  shape (`https` + `:` + the rest), and a legal bare-URL line is a link line,
 *  never an unknown key. Recognized params never reach here — `readParamLine`
 *  already returned true for them. */
function countDroppedKey(input: { target: ParseAccumulator; line: string }): void {
  const match = input.line.match(ATTACH_PARAM_REGEX);
  if (match === null) return;
  const key = (match[1] ?? '').toLowerCase();
  if (ATTACH_URL_SCHEME_HEAD_REGEX.test(input.line)) return;
  input.target.droppedKeyCounts.set(
    key,
    (input.target.droppedKeyCounts.get(key) ?? 0) + 1,
  );
}

/**
 * One `attach-result` block body.
 *
 * Returns null when the block offered no link at all: an attach with nothing
 * to attach is not a lenient parse of something, it is an empty block, and the
 * write seam has nothing to materialise from it.
 */
export function parseAttachBlock(raw: string): ParsedAttachBlock | null {
  // Defensive ceiling on the whole block — a pathological block is never even
  // split into lines past this point.
  const bounded =
    raw.length > ATTACH_MAX_INPUT_LENGTH ? raw.slice(0, ATTACH_MAX_INPUT_LENGTH) : raw;

  const target: ParseAccumulator = {
    title: null,
    links: [],
    seenUrls: new Set<string>(),
    droppedLinkCount: 0,
    droppedKeyCounts: new Map<string, number>(),
    inLinksRegion: false,
  };

  bounded.split('\n').forEach((rawLine) => {
    // Defensive ceiling per line. Skipped outright rather than truncated, so a
    // half-read address can never become a link nobody wrote.
    if (rawLine.length > ATTACH_MAX_LINE_LENGTH) return;

    const line = normalizeLine({ rawLine });
    if (line.length === 0) return;
    if (readParamLine({ target, line })) return;

    // Not a recognized param. Inside the links region every address counts;
    // outside it only a markdown item does — a bare URL in prose is prose.
    // Link reading is untouched by the key accounting that follows it (ruled
    // 2026-08-25, decision 1: a URL inside an annotation line keeps being
    // collected; the notice is what surfaces the absorption).
    collectLinksFrom({ target, text: line, allowBareUrls: target.inLinksRegion });
    countDroppedKey({ target, line });
  });

  if (target.links.length === 0) return null;
  return {
    title: target.title,
    links: target.links,
    droppedLinkCount: target.droppedLinkCount,
    droppedKeys: [...target.droppedKeyCounts.entries()].map(([key, count]) => ({
      key,
      count,
    })),
  };
}

/** Every authored attach block in a document, located and parsed, in document
 *  order. Blocks that offered no link are absent — there is nothing to
 *  materialise from one. */
export function locateAttachBlocks(input: { markdown: string }): LocatedAttachBlock[] {
  return fenceSpansOfKind({ markdown: input.markdown, info: ATTACH_FENCE_INFO_STRING })
    .map((span) => {
      const parsed = parseAttachBlock(span.body);
      return parsed === null ? null : { span, parsed };
    })
    .filter((located): located is LocatedAttachBlock => located !== null);
}
